// Wellness Quiz section layout data — follows the same COMPONENT_REGISTRY
// pattern as thankYou: a single visual design (no layout picker), admin
// edits are the quiz's own content (questions/answers/section image)
// instead of a visual-layout choice. See quiz-plan.md Section 5.2.
//
// Every question's/answer's text field is `translatable: true` — exact same
// {en, ru, ar, ge} pattern as FAQ's question/answer fields
// (faqLayouts.constants.js) — read/written by the same CardsEditor /
// NestedListEditor machinery, no new translation mechanism.
import { packCategoryService } from "../../../services/settings-services/packCategory.service";
import { resolveTranslatableValue } from "./translatable.util";

// Seed content mirrors today's live hardcoded quiz
// (meelz-client-v2/src/components/wellnessQuiz/takeTheQuiz.tsx) so first
// publish doesn't blank out the live quiz — see quiz-plan.md Section 7.3.
// `preferredPackCategoryId` is left blank in the seed (no safe brand-
// agnostic default id exists) — an admin must pick real categories via the
// asyncSelect before scoring can resolve anything for these seeded answers.
// Section-chrome/flow text (Section 10) — seeded verbatim from the live
// i18n `quiz` namespace (meelz-client-v2/src/messages/en.json) so first
// publish is visually identical to today; client falls back to its own
// t("...") call whenever a config value is blank (see quiz-plan.md Section
// 10.2), so these are safe to leave blank on any row saved before this
// section shipped.
const QUIZ_DEFAULT_PROPS = {
    // Color fields (same convention as FAQ's backgroundColor/primaryColor,
    // faqLayouts.constants.js) — the real client previously had NO
    // config-driven color at all, hardcoded to the site's real MEELZ brand
    // tokens (meelz-client-v2/src/styles/globals.css: --color-primary:
    // #573121, --color-bg: #F9F3E6, the quiz container's actual `bg-bg`
    // class). Defaults match those real brand values exactly, so publishing
    // for the first time doesn't visually change anything.
    backgroundColor: "#F9F3E6",
    textColor:       "#1a1a1a",
    iconColor:       "#aaaaaa",
    primaryColor: "#573121",
    // Same asset-id-style indirection as hero's `image` field
    // (HERO_IMAGE_OPTIONS) — "meelz-quiz-default-meal" resolves to
    // /meal-1.png on the real client (takeTheQuiz.tsx's own hardcoded
    // fallback path), so picking it here doesn't change anything live.
    sectionImage: "meelz-quiz-default-meal",
    sectionImageSource: "library",
    sectionTitle: { en: "Take the Quiz", ru: "Пройти тест", ar: "ابدأ الاختبار", ge: "ტესტის ჩატარება" },
    sectionBadge: { en: "Personalised Nutrition" },
    sectionSubtitle: { en: "Answer a few quick questions and we'll match you with the ideal calorie plan and diet programme for your goals." },
    takeQuizLabel: { en: "Take the Quiz" },
    bookConsultationLabel: { en: "Book a Consultation" },
    contactStepTitle: { en: "Where would you like us to send your plan?" },
    contactFullNameLabel: { en: "Full Name" },
    contactFullNameRequired: true,
    contactEmailLabel: { en: "Email Address" },
    contactEmailRequired: true,
    contactPhoneLabel: { en: "Mobile Number" },
    contactPhoneRequired: true,
    contactExtraFields: [],
    backButtonLabel: { en: "Back" },
    continueButtonLabel: { en: "Continue" },
    getMyPlanButtonLabel: { en: "Get My Plan" },
    processingLabel: { en: "Processing…" },
    errorFieldsText: { en: "Please fill in all fields to continue." },
    errorSelectionText: { en: "Please make a selection to continue." },
    thankYouTitle: { en: "Thank you, {name}! 🎉" },
    thankYouBodyText: { en: "We've received your responses. Your personalised wellness plan will be sent to you shortly." },
    retakeButtonLabel: { en: "Retake Quiz" },
    // Full 6-question seed, mirroring the old hardcoded quiz's real content
    // one-for-one (meelz-client-v2/src/messages/en.json "quiz" namespace,
    // goal/focus/lifestyle/activity/calorie/meats/health keys) so this
    // Builder-driven quiz asks the same real questions the live site always
    // has, instead of the earlier 1-question placeholder. `emoji` values are
    // real icon-picker names (iconLibrary.constants.js / HERO_ICON_MAP),
    // not literal emoji characters.
    // preferredPackCategoryId values below map each non-calorie answer to
    // one of the real, already-existing pack_categories rows (STANDARD/
    // SLIM/MUSCLE GAIN/LCHF/VEGETARIAN/SPORT/BALANCE/DETOX — c1000000-...
    // ids) so scoring (wellness_quiz.service.ts's majority-vote across
    // Q1-Q4/Q6/Q7) has real thematic targets to resolve instead of nulls.
    // The calorie question (q5) intentionally has NO preferredPackCategoryId
    // on any answer — it never votes on category, only on which Pack is
    // picked within whichever category wins (see QUIZ_CALORIE_ANSWER_SUB_FIELDS).
    questions: [
        {
            id: "q1",
            question: { en: "What is your primary wellness goal?" },
            icon: "AimOutlined",
            answers: [
                { id: "q1a1", label: { en: "Lose weight" }, emoji: "LuTrendingDown", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000003" },
                { id: "q1a2", label: { en: "Maintain weight" }, emoji: "LuScale", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000004" },
                { id: "q1a3", label: { en: "Gain muscles" }, emoji: "LuDumbbell", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000005" },
                { id: "q1a4", label: { en: "Improve health" }, emoji: "HeartOutlined", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000001" },
            ],
        },
        {
            id: "q2",
            question: { en: "What is your dietary focus to achieve your wellness goal?" },
            icon: "LuSalad",
            answers: [
                { id: "q2a1", label: { en: "High-protein intake" }, emoji: "GiSteak", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000005" },
                { id: "q2a2", label: { en: "Plant-based diet" }, emoji: "GiBroccoli", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000007" },
                { id: "q2a3", label: { en: "Time-restricted eating" }, emoji: "ClockCircleOutlined", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000009" },
                { id: "q2a4", label: { en: "Low-sugar consumption" }, emoji: "LuCandyOff", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000006" },
                { id: "q2a5", label: { en: "Low-calorie diet" }, emoji: "LuFlame", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000003" },
            ],
        },
        {
            id: "q3",
            question: { en: "Which description aligns most with your current status or lifestyle?" },
            icon: "TeamOutlined",
            answers: [
                { id: "q3a1", label: { en: "Busy professional" }, emoji: "LuBriefcase", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000011" },
                { id: "q3a2", label: { en: "Pregnant / Breastfeeding" }, emoji: "LuBaby", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000004" },
                { id: "q3a3", label: { en: "Sports fanatic" }, emoji: "LuDumbbell", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000002" },
                { id: "q3a4", label: { en: "Student" }, emoji: "LuGraduationCap", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000001" },
                { id: "q3a5", label: { en: "Housewife" }, emoji: "LuSofa", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000004" },
                { id: "q3a6", label: { en: "Remote worker" }, emoji: "LuLaptop", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000008" },
            ],
        },
        {
            id: "q4",
            question: { en: "How would you describe your level of physical activity?" },
            icon: "LuActivity",
            answers: [
                { id: "q4a1", label: { en: "Sedentary (little or no exercise)" }, emoji: "LuBed", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000003" },
                { id: "q4a2", label: { en: "Lightly active (light exercise, 1–3 days/week)" }, emoji: "LuFootprints", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000001" },
                { id: "q4a3", label: { en: "Moderately active (moderate exercise, 3–5 days/week)" }, emoji: "LuBike", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000004" },
                { id: "q4a4", label: { en: "Very active (intense exercise, 3–5 days/week)" }, emoji: "LuHeartPulse", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000002" },
                { id: "q4a5", label: { en: "Extremely active (intense exercise, 6–7 days/week)" }, emoji: "ThunderboltOutlined", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000005" },
            ],
        },
        {
            id: "q5",
            // Internal marker (not admin-editable — absent from
            // QUIZ_QUESTION_FIELDS) identifying this as THE calorie question,
            // so the backend's resolveRecommendation can find it by role
            // rather than by hardcoding the seed id "q5" (ids can drift once
            // an admin edits/republishes). See minCalorie/maxCalorie below.
            questionType: "calorie",
            question: { en: "What daily calorie range suits your goals?" },
            icon: "LuFlame",
            answers: [
                { id: "q5a1", label: { en: "Under 1,200 kcal" }, emoji: "LuFlame", minCalorie: null, maxCalorie: 1200 },
                { id: "q5a2", label: { en: "1,200 – 1,500 kcal" }, emoji: "LuFlame", minCalorie: 1200, maxCalorie: 1500 },
                { id: "q5a3", label: { en: "1,500 – 1,800 kcal" }, emoji: "LuFlame", minCalorie: 1500, maxCalorie: 1800 },
                { id: "q5a4", label: { en: "1,800 – 2,200 kcal" }, emoji: "LuFlame", minCalorie: 1800, maxCalorie: 2200 },
                { id: "q5a5", label: { en: "2,200 – 2,800 kcal" }, emoji: "LuFlame", minCalorie: 2200, maxCalorie: 2800 },
                { id: "q5a6", label: { en: "2,800+ kcal" }, emoji: "LuFlame", minCalorie: 2800, maxCalorie: null },
            ],
        },
        {
            id: "q6",
            question: { en: "Which type(s) of meat do you exclude from your diet?" },
            icon: "GiMeat",
            answers: [
                { id: "q6a1", label: { en: "None (I eat all types of meat)" }, emoji: "GiMeat", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000001" },
                { id: "q6a2", label: { en: "Red Meat" }, emoji: "GiSteak", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000007" },
                { id: "q6a3", label: { en: "Poultry" }, emoji: "GiChickenLeg", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000006" },
                { id: "q6a4", label: { en: "Seafood" }, emoji: "GiShrimp", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000004" },
            ],
        },
        {
            id: "q7",
            question: { en: "Do you currently have any medical condition(s)?" },
            icon: "MedicineBoxOutlined",
            answers: [
                { id: "q7a1", label: { en: "No medical conditions" }, emoji: "CheckCircleOutlined", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000001" },
                { id: "q7a2", label: { en: "Diabetes" }, emoji: "LuDroplet", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000006" },
                { id: "q7a3", label: { en: "PCOS" }, emoji: "LuShieldAlert", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000003" },
                { id: "q7a4", label: { en: "Metabolic Syndrome" }, emoji: "LuActivity", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000009" },
                { id: "q7a5", label: { en: "Heart Disease" }, emoji: "LuHeartCrack", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000004" },
                { id: "q7a6", label: { en: "High Blood Pressure" }, emoji: "LuGauge", followUpText: { en: "" }, preferredPackCategoryId: "c1000000-0000-0000-0000-000000000004" },
            ],
        },
    ],
};

// CK's real seed content — mirrors today's live hardcoded CK quiz
// (ck-client-ck's wellness-quiz-section.tsx + wellness-quiz/take-the-quiz.tsx,
// text pulled verbatim from lib/dictionary.ts's quizKeys block) so first
// publish for CK doesn't change what visitors see. CK's flow has no
// separate "dietary focus" step and no calorie-linked pack targeting the
// way MEELZ's seed does — it's 5 real questions (goal/activity/calorie/
// meats/health) plus the fixed contact step, matching CK's actual current
// site structure one-for-one, not MEELZ's 7-question structure.
// `preferredPackCategoryId` is left blank on every answer (same reasoning
// as MEELZ's seed above) — an admin must pick CK's real live PackCategory
// rows via the asyncSelect before scoring can resolve anything. The calorie
// question uses `questionType: "calorie"` with minCalorie/maxCalorie,
// matching MEELZ's q5 convention exactly, so the same backend scoring path
// works unmodified for CK once published — CK's client submits the same
// generic `{ answers, fullName, email, phone }` shape MEELZ already does,
// no longer its old flat `{ primaryGoal, activityLevel, ... }` payload (see
// take-the-quiz.tsx's rewrite for the client side of this change).
// Icon values are real ICON_LIBRARY names (iconLibrary.constants.js) — no
// new icons were added; every value below already exists in that library
// and was chosen as the closest available match to CK's original literal
// emoji per answer.
const CK_QUIZ_DEFAULT_PROPS = {
    backgroundColor: "#f0f7ec",
    textColor:       "#1a1a1a",
    primaryColor: "#007C37",
    sectionImage: "meelz-quiz-default-meal",
    sectionImageSource: "library",
    sectionTitle: { en: "Find Your Perfect Meal Plan", ru: "Найдите свой идеальный рацион", ge: "იპოვეთ თქვენთვის იდეალური რაციონი" },
    sectionBadge: { en: "Personalised Nutrition", ru: "Персональное питание", ge: "პერსონალური კვება" },
    sectionSubtitle: { en: "Answer a few quick questions and we'll match you with the ideal diet and calorie plan", ru: "Ответьте на несколько вопросов, и мы подберём для вас идеальный рацион и калорийность", ge: "უპასუხეთ რამდენიმე კითხვას და შევარჩევთ თქვენთვის იდეალურ დიეტასა და კალორიულობას" },
    takeQuizLabel: { en: "Take the Quiz", ru: "Пройти опрос", ge: "გაიარეთ ტესტი" },
    bookConsultationLabel: { en: "Book a Consultation", ru: "Записаться на консультацию", ge: "დაჯავშნეთ კონსულტაცია" },
    contactStepTitle: { en: "Where would you like us to send your plan?", ru: "Куда отправить ваш рацион?", ge: "სად გსურთ, გამოგიგზავნოთ თქვენი გეგმა?" },
    contactFullNameLabel: { en: "Full Name", ru: "Полное имя", ge: "სრული სახელი" },
    contactFullNameRequired: true,
    contactEmailLabel: { en: "Email Address", ru: "Электронная почта", ge: "ელ. ფოსტა" },
    contactEmailRequired: true,
    contactPhoneLabel: { en: "Mobile Number", ru: "Номер телефона", ge: "მობილურის ნომერი" },
    contactPhoneRequired: true,
    contactExtraFields: [],
    backButtonLabel: { en: "Back", ru: "Назад", ge: "უკან" },
    continueButtonLabel: { en: "Continue", ru: "Продолжить", ge: "გაგრძელება" },
    getMyPlanButtonLabel: { en: "Get My Plan", ru: "Получить мой рацион", ge: "მივიღო ჩემი გეგმა" },
    processingLabel: { en: "Processing…", ru: "Обработка…", ge: "მუშავდება…" },
    errorFieldsText: { en: "Please fill in all fields to continue.", ru: "Пожалуйста, заполните все поля, чтобы продолжить.", ge: "გასაგრძელებლად გთხოვთ, შეავსოთ ყველა ველი." },
    errorSelectionText: { en: "Please make a selection to continue.", ru: "Пожалуйста, сделайте выбор, чтобы продолжить.", ge: "გასაგრძელებლად გთხოვთ, გააკეთოთ არჩევანი." },
    thankYouTitle: { en: "Thank you, {name}!", ru: "Спасибо, {name}!", ge: "გმადლობთ, {name}!" },
    thankYouBodyText: { en: "We've received your responses. Your personalised meal plan is ready below.", ru: "Мы получили ваши ответы. Ваш персональный план питания подобран ниже.", ge: "მივიღეთ თქვენი პასუხები. თქვენი პერსონალური კვების გეგმა მზადაა ქვემოთ." },
    retakeButtonLabel: { en: "Retake Quiz", ru: "Пройти опрос заново", ge: "თავიდან გავლა" },
    questions: [
        {
            id: "ckq1",
            question: { en: "What is your primary goal?", ru: "Какова ваша основная цель?", ge: "რა არის თქვენი მთავარი მიზანი?" },
            icon: "AimOutlined",
            answers: [
                { id: "ckq1a1", label: { en: "Lose weight", ru: "Похудеть", ge: "წონის დაკლება" }, emoji: "LuTrendingDown", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq1a2", label: { en: "Ready-to-eat meals, no cooking", ru: "Готовые блюда без забот", ge: "მზა კერძები, მომზადების გარეშე" }, emoji: "GiOpenedFoodCan", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq1a3", label: { en: "Sports & muscle building", ru: "Спорт и набор мышечной массы", ge: "სპორტი და კუნთოვანი მასის მატება" }, emoji: "LuDumbbell", followUpText: { en: "" }, preferredPackCategoryId: "" },
            ],
        },
        {
            id: "ckq2",
            question: { en: "How would you describe your level of physical activity?", ru: "Как бы вы описали свой уровень физической активности?", ge: "როგორ აღწერდით თქვენს ფიზიკური აქტივობის დონეს?" },
            icon: "LuActivity",
            answers: [
                { id: "ckq2a1", label: { en: "Sedentary (little or no exercise)", ru: "Малоподвижный (мало или нет тренировок)", ge: "მცირემოძრავი (მცირე ან საერთოდ არ ვვარჯიშობ)" }, emoji: "LuSofa", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq2a2", label: { en: "Lightly active (light exercise, 1–3 days/week)", ru: "Лёгкая активность (1–3 дня в неделю)", ge: "მსუბუქი აქტივობა (კვირაში 1–3 დღე)" }, emoji: "LuFootprints", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq2a3", label: { en: "Moderately active (moderate exercise, 3–5 days/week)", ru: "Умеренная активность (3–5 дней в неделю)", ge: "საშუალო აქტივობა (კვირაში 3–5 დღე)" }, emoji: "LuActivity", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq2a4", label: { en: "Very active (intense exercise, 3–5 days/week)", ru: "Высокая активность (интенсивные тренировки 3–5 дней в неделю)", ge: "მაღალი აქტივობა (ინტენსიური ვარჯიში კვირაში 3–5 დღე)" }, emoji: "LuFlame", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq2a5", label: { en: "Extremely active (intense exercise, 6–7 days/week)", ru: "Экстремальная активность (интенсивные тренировки 6–7 дней в неделю)", ge: "უკიდურესი აქტივობა (ინტენსიური ვარჯიში კვირაში 6–7 დღე)" }, emoji: "ThunderboltOutlined", followUpText: { en: "" }, preferredPackCategoryId: "" },
            ],
        },
        {
            id: "ckq3",
            questionType: "calorie",
            question: { en: "What daily calorie range suits your goals?", ru: "Какой диапазон калорий подходит для ваших целей?", ge: "რომელი დღიური კალორიულობა შეესაბამება თქვენს მიზნებს?" },
            icon: "LuFlame",
            answers: [
                { id: "ckq3a1", label: { en: "Under 1,200 kcal", ru: "До 1 200 ккал", ge: "1 200 კკალ-ზე ნაკლები" }, emoji: "LuSalad", minCalorie: null, maxCalorie: 1200 },
                { id: "ckq3a2", label: { en: "1,200 – 1,500 kcal", ru: "1 200 — 1 500 ккал", ge: "1 200 — 1 500 კკალ" }, emoji: "LuSalad", minCalorie: 1200, maxCalorie: 1500 },
                { id: "ckq3a3", label: { en: "1,500 – 1,800 kcal", ru: "1 500 — 1 800 ккал", ge: "1 500 — 1 800 კკალ" }, emoji: "GiBowlOfRice", minCalorie: 1500, maxCalorie: 1800 },
                { id: "ckq3a4", label: { en: "1,800 – 2,200 kcal", ru: "1 800 — 2 200 ккал", ge: "1 800 — 2 200 კკალ" }, emoji: "LuActivity", minCalorie: 1800, maxCalorie: 2200 },
                { id: "ckq3a5", label: { en: "2,200 – 2,800 kcal", ru: "2 200 — 2 800 ккал", ge: "2 200 — 2 800 კკალ" }, emoji: "LuFlame", minCalorie: 2200, maxCalorie: 2800 },
                { id: "ckq3a6", label: { en: "2,800+ kcal", ru: "2 800+ ккал", ge: "2 800+ კკალ" }, emoji: "LuDumbbell", minCalorie: 2800, maxCalorie: null },
            ],
        },
        {
            id: "ckq4",
            question: { en: "Which type(s) of meat do you exclude from your diet?", ru: "Какие виды мяса вы исключаете из рациона?", ge: "რომელი ხორცის სახეობებს გამორიცხავთ თქვენი კვებიდან?" },
            icon: "GiMeat",
            answers: [
                { id: "ckq4a1", label: { en: "None (I eat all types of meat)", ru: "Никакие (ем всё мясо)", ge: "არცერთს (ვჭამ ყველა სახის ხორცს)" }, emoji: "CheckCircleOutlined", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq4a2", label: { en: "Red Meat", ru: "Красное мясо", ge: "წითელი ხორცი" }, emoji: "GiSteak", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq4a3", label: { en: "Poultry", ru: "Птица", ge: "ფრინველი" }, emoji: "GiChickenLeg", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq4a4", label: { en: "Seafood", ru: "Морепродукты", ge: "ზღვის პროდუქტები" }, emoji: "GiShrimp", followUpText: { en: "" }, preferredPackCategoryId: "" },
            ],
        },
        {
            id: "ckq5",
            question: { en: "Do you currently have any medical condition(s)?", ru: "Есть ли у вас какие-либо заболевания?", ge: "გაქვთ თუ არა რაიმე სამედიცინო მდგომარეობა?" },
            icon: "MedicineBoxOutlined",
            answers: [
                { id: "ckq5a1", label: { en: "No medical conditions", ru: "Нет заболеваний", ge: "სამედიცინო მდგომარეობა არ მაქვს" }, emoji: "CheckCircleOutlined", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq5a2", label: { en: "Diabetes", ru: "Диабет", ge: "დიაბეტი" }, emoji: "LuDroplet", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq5a3", label: { en: "PCOS", ru: "СПКЯ", ge: "PCOS" }, emoji: "MedicineBoxOutlined", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq5a4", label: { en: "Metabolic Syndrome", ru: "Метаболический синдром", ge: "მეტაბოლური სინდრომი" }, emoji: "MedicineBoxOutlined", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq5a5", label: { en: "Heart Disease", ru: "Заболевание сердца", ge: "გულის დაავადება" }, emoji: "LuHeartCrack", followUpText: { en: "" }, preferredPackCategoryId: "" },
                { id: "ckq5a6", label: { en: "High Blood Pressure", ru: "Высокое давление", ge: "მაღალი წნევა" }, emoji: "LuHeartPulse", followUpText: { en: "" }, preferredPackCategoryId: "" },
            ],
        },
    ],
};

// Exported so componentRegistry.js's FULL_CONTENT_OVERRIDE_BY_KEY_AND_BRAND
// can reference it directly (quiz -> CK), same precedent as
// FAQ_CK_DEFAULT_PROPS — CK's real quiz content applied uniformly across
// whichever layout id is active, instead of a separate selectable layout.
export const QUIZ_CK_DEFAULT_PROPS = CK_QUIZ_DEFAULT_PROPS;

export const QUIZ_LAYOUTS = {
    default: { label: "Wellness Quiz", defaultProps: QUIZ_DEFAULT_PROPS },
};

// Same asset-id-style indirection as HERO_IMAGE_OPTIONS/
// HERO_IMAGE_ADMIN_PREVIEW_PATHS (heroLayouts.constants.js): a stable id is
// stored in `sectionImage`, and each side (this admin dashboard,
// meelz-client-v2) resolves it to its own local path. "meelz-quiz-default-
// meal" -> /meal-1.png on the real client, matching takeTheQuiz.tsx's own
// hardcoded fallback image exactly.
export const QUIZ_IMAGE_OPTIONS = [
    { value: "meelz-quiz-default-meal", label: "Default meal photo" },
];

// gwt-admin's OWN preview-only path (public/hero-images/, reusing the same
// folder Hero's preview assets already live in rather than a new one).
export const QUIZ_IMAGE_ADMIN_PREVIEW_PATHS = {
    "meelz-quiz-default-meal": "/hero-images/meelz-quiz-meal.png",
};

// Fetches PackCategory options for the asyncSelect, in the shape
// AsyncSelectField/NestedListEditor expect: { value: id, label }.
// Language-agnostic label (always English, or first available) — the
// stored value is the id, never a name, so which language shows in the
// admin's own picker doesn't affect what's actually persisted/scored.
function fetchPackCategoryOptions() {
    return packCategoryService.getAllLite().then((categories) =>
        (categories || [])
            // Inactive categories can never actually be recommended by the
            // quiz (wellness_quiz.service.ts excludes them from the vote
            // tally), so don't let an admin pick one here either.
            .filter((c) => c.isActive)
            .map((c) => ({
                value: c.id,
                label: resolveTranslatableValue(c.name, "en") || c.id,
            }))
    );
}

export const QUIZ_ANSWER_SUB_FIELDS = [
    { field: "label", label: "Answer", translatable: true },
    { field: "emoji", label: "Emoji", type: "icon" },
    { field: "followUpText", label: "Follow-up text", translatable: true },
    { field: "preferredPackCategoryId", label: "Preferred plan", type: "asyncSelect", fetchOptions: fetchPackCategoryOptions },
];

// Only used for the calorie question's (q5) answers — see
// QUIZ_QUESTION_FIELDS's `answers` subFields resolver below. Deliberately
// does NOT include the base QUIZ_ANSWER_SUB_FIELDS: the calorie question's
// answer text is auto-generated from Min/Max (see calorieRange/
// CalorieRangeField in NestedListEditor.js) rather than freehand-typed, it
// has no follow-up text, and it casts no category vote at all — the
// recommended PackCategory comes purely from the other 6 questions
// (wellness_quiz.service.ts skips questionType: "calorie" in its vote-count
// loop). minCalorie/maxCalorie are read by the backend to pick a
// calorie-appropriate Pack within whichever category wins, instead of
// always the cheapest one.
export const QUIZ_CALORIE_ANSWER_SUB_FIELDS = [
    { field: "emoji", label: "Emoji", type: "icon" },
    { field: "calorieRange", label: "Calorie range", type: "calorieRange" },
];

export const QUIZ_ANSWER_NEW_DEFAULT = {
    id: null, // real id assigned on add — see quizAnswerIdGenerator below
    label: { en: "New answer" },
    emoji: "",
    followUpText: { en: "" },
    preferredPackCategoryId: "",
    minCalorie: null,
    maxCalorie: null,
};

export const QUIZ_QUESTION_FIELDS = [
    { field: "question", label: "Question", translatable: true, wrapControl: true, multiline: true },
    { field: "icon", label: "Icon", type: "icon" },
    {
        field: "answers",
        label: "Answers",
        type: "list",
        // Per-card resolver (see CardsEditor.js's CardItem) — only the
        // calorie question (questionType: "calorie") gets the calorie-range
        // control; every other question renders the plain base fields.
        subFields: (card) => card.questionType === "calorie" ? QUIZ_CALORIE_ANSWER_SUB_FIELDS : QUIZ_ANSWER_SUB_FIELDS,
        subNewItemDefault: QUIZ_ANSWER_NEW_DEFAULT,
    },
];

export const QUIZ_QUESTION_NEW_DEFAULT = {
    id: null, // real id assigned on add — see quizQuestionIdGenerator below
    question: { en: "New Question" },
    icon: "QuestionCircleOutlined",
    answers: [],
};

// Admin-defined extra contact fields (Instagram handle, Telegram ID, etc.) —
// a fully open-ended list, unlike fullName/email/phone which stay fixed on
// both the client and the backend DTO. Each card's `label` doubles as the
// input's placeholder/label on the client; `id` (assigned on add, same
// null-id-generation convention as questions/answers) is the stable key the
// value is stored under in the submission's `extraContactFields` map and
// what the backend's required-field check (wellness_quiz.service.ts) reads.
export const QUIZ_CONTACT_EXTRA_FIELD_FIELDS = [
    { field: "label", label: "Field Label", translatable: true },
    {
        field: "fieldType",
        label: "Input Type",
        type: "select",
        options: [
            { value: "text", label: "Text" },
            { value: "email", label: "Email" },
            { value: "tel", label: "Phone" },
            { value: "url", label: "URL" },
        ],
    },
    { field: "required", label: "Required", type: "boolean" },
];

export const QUIZ_CONTACT_EXTRA_FIELD_NEW_DEFAULT = {
    id: null, // real id assigned on add, same convention as QUIZ_QUESTION_NEW_DEFAULT
    label: { en: "New Field" },
    fieldType: "text",
    required: false,
};

export const QUIZ_SCHEMA = [
    { key: "backgroundColor", label: "Background color", type: "color" },
    { key: "textColor",       label: "Text color",       type: "color" },
    { key: "iconColor",       label: "Icon color",        type: "color" },
    { key: "primaryColor", label: "Primary color", type: "color" },
    { key: "sectionImage", label: "Section image", type: "imagePicker", options: QUIZ_IMAGE_OPTIONS, previewPaths: QUIZ_IMAGE_ADMIN_PREVIEW_PATHS },
    { key: "sectionBadge", label: "Section badge", type: "text", translatable: true, wrapControl: true },
    { key: "sectionTitle", label: "Section title", type: "text", translatable: true, wrapControl: true },
    { key: "sectionSubtitle", label: "Section subtitle", type: "text", translatable: true, wrapControl: true },
    { key: "takeQuizLabel", label: "\"Take the Quiz\" tab label", type: "text", translatable: true, wrapControl: true },
    { key: "bookConsultationLabel", label: "\"Book a Consultation\" tab label", type: "text", translatable: true, wrapControl: true },
    {
        key: "questions",
        label: "Questions",
        type: "cards",
        fields: QUIZ_QUESTION_FIELDS,
        newItemDefault: QUIZ_QUESTION_NEW_DEFAULT,
        cardLabel: "Question",
        // The calorie question can't be deleted — the calorie-aware
        // pack-selection scoring in wellness_quiz.service.ts depends on it
        // always existing. Admin can still freely edit its wording/icon/
        // answers, just not remove the question card itself.
        //
        // Locked by `questionType === "calorie"` (a predicate, not a fixed
        // id) — confirmed bug with the previous `["q5"]` id-list approach:
        // MEELZ's default seed's calorie question really is id "q5", but
        // CK's real published content (QUIZ_CK_DEFAULT_PROPS below) uses
        // "ckq3" for the exact same question. A single hardcoded id can only
        // ever match one brand's content, so CK's calorie question was
        // silently deletable while MEELZ's/the default's wasn't — matching
        // the reported "sometimes locked" discrepancy between brands.
        // Matching on the question's own `questionType` field instead is
        // correct regardless of id, brand, or question reordering.
        nonDeletableIds: (card) => card.questionType === "calorie",
    },
    { key: "contactSectionHeading", label: "Contact Details", type: "sectionHeader" },
    { key: "contactStepTitle", label: "Contact step title", type: "text", translatable: true },
    { key: "contactFullNameLabel", label: "\"Full Name\" field label", type: "text", translatable: true },
    { key: "contactFullNameRequired", label: "\"Full Name\" required", type: "boolean" },
    { key: "contactEmailLabel", label: "\"Email\" field label", type: "text", translatable: true },
    { key: "contactEmailRequired", label: "\"Email\" required", type: "boolean" },
    { key: "contactPhoneLabel", label: "\"Phone\" field label", type: "text", translatable: true },
    { key: "contactPhoneRequired", label: "\"Phone\" required", type: "boolean" },
    {
        key: "contactExtraFields",
        label: "Additional Contact Fields",
        type: "cards",
        fields: QUIZ_CONTACT_EXTRA_FIELD_FIELDS,
        newItemDefault: QUIZ_CONTACT_EXTRA_FIELD_NEW_DEFAULT,
        cardLabel: "Field",
    },
    { key: "backButtonLabel", label: "\"Back\" button label", type: "text", translatable: true, wrapControl: true },
    { key: "continueButtonLabel", label: "\"Continue\" button label", type: "text", translatable: true, wrapControl: true },
    { key: "getMyPlanButtonLabel", label: "\"Get My Plan\" button label", type: "text", translatable: true, wrapControl: true },
    { key: "processingLabel", label: "Submitting-state label", type: "text", translatable: true, wrapControl: true },
    { key: "errorFieldsText", label: "Contact-step validation error", type: "text", translatable: true, wrapControl: true },
    { key: "errorSelectionText", label: "Answer-selection validation error", type: "text", translatable: true, wrapControl: true },
    { key: "thankYouTitle", label: "Success screen title (keep {name})", type: "text", translatable: true, wrapControl: true },
    { key: "thankYouBodyText", label: "Success screen body", type: "text", translatable: true, wrapControl: true },
    { key: "retakeButtonLabel", label: "\"Retake Quiz\" button label", type: "text", translatable: true, wrapControl: true },
];
